import React, { useState } from 'react';
import { TourQuestionnaire, TouristSite, Settings } from '../../../types';
import { CheckCircle2, Clock } from 'lucide-react';

interface Step9_1Props {
  questionnaire: TourQuestionnaire;
  setQuestionnaire: React.Dispatch<React.SetStateAction<TourQuestionnaire>>;
  sites: TouristSite[];
  settings: Settings;
  driverHomeLocation: { lat: number; lng: number } | null;
  idealDays: number;
  setStep: (step: number) => void;
  handleNext: (acceptDrop?: boolean, questionnaireOverride?: Partial<TourQuestionnaire>) => void;
  handleBack: () => void;
}

export const Step9_1: React.FC<Step9_1Props> = ({
  questionnaire,
  setQuestionnaire,
  sites,
  settings,
  driverHomeLocation,
  idealDays,
  setStep,
  handleNext,
  handleBack,
}) => {
  const departureHour = parseInt(questionnaire.departureTime.split(':')[0]);
  const isLateDeparture = departureHour >= 11;

  const [selectedDays, setSelectedDays] = useState<number | null>(
    questionnaire.logistics?.days || null
  );

  const [departureTimes, setDepartureTimes] = useState<Record<number, string>>(
    questionnaire.departureTimes || {}
  );

  const handleDepartureTimeChange = (day: number, time: string) => {
    setDepartureTimes(prev => ({ ...prev, [day]: time }));
  };

  // Build ALL options from 1 to idealDays in steps of 0.5
  // We always show 1 day + every 0.5 increment up to idealDays (capped at 10 for sanity)
  const buildOptions = (): number[] => {
    const options: number[] = [];
    const cap = Math.min(idealDays, 10);
    // Steps: 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, etc. up to idealDays
    let d = 1.5;
    while (d <= cap + 0.01) {
      options.push(Math.round(d * 2) / 2); // round to nearest 0.5
      d += 0.5;
    }
    // Ensure idealDays itself is always in the list
    if (!options.some(o => Math.abs(o - idealDays) < 0.01)) {
      options.push(idealDays);
      options.sort((a, b) => a - b);
    }
    return options;
  };

  const multiDayOptions = buildOptions();

  // For a given number of chosen days, is it below ideal (needs ranking) or equal/above (all fit)?
  const needsRanking = (days: number) => days < idealDays;
  const allFit = (days: number) => days >= idealDays;

  // Valid when a selection is made AND departure times filled for all days from 2 onwards
  const extraDaysNeeded = selectedDays !== null && selectedDays > 1
    ? Array.from({ length: Math.ceil(selectedDays) - 1 }, (_, i) => i + 2)
    : [];
  const isValid = selectedDays !== null &&
    extraDaysNeeded.every(d => departureTimes[d] && departureTimes[d].trim() !== '');

  const handleConfirm = () => {
    if (selectedDays === null) return;
    const updatedQ: Partial<TourQuestionnaire> = {
      logistics: { ...questionnaire.logistics, days: selectedDays },
      departureTimes: selectedDays > 1 ? departureTimes : {},
    };
    setQuestionnaire(prev => ({ ...prev, ...updatedQ }));
    // Always acceptDrop=true — tunnel decides 9.5 or 9.8 based on selectedDays vs idealDays
    handleNext(true, updatedQ);
  };

  const formatDays = (d: number) =>
    Number.isInteger(d) ? `${d} jour${d > 1 ? 's' : ''}` : `${d} jours`;

  return (
    <div className="flex flex-col h-[100dvh] p-0 overflow-hidden bg-zinc-950">
      <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar pb-32">
        <div className="space-y-2">
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
            Itinéraire trop long
          </h2>
          <p className="text-zinc-400">
            Votre sélection nécessite environ{' '}
            <span className="text-emerald-400 font-bold">
              {formatDays(idealDays)}
            </span>{' '}
            pour tout réaliser confortablement. Choisissez la durée qui vous convient.
          </p>
        </div>

        <div className="space-y-3">

          {/* Option: depart earlier to fit in 1 day */}
          {isLateDeparture && (
            <button
              onClick={() => {
                const updated: Partial<TourQuestionnaire> = {
                  departureTime: '09:00',
                  logistics: { ...questionnaire.logistics, days: 1 },
                  departureTimes: {},
                };
                setQuestionnaire(prev => ({ ...prev, ...updated }));
                handleNext(true, updated);
              }}
              className="w-full p-4 bg-blue-500/10 border border-blue-500/30 text-blue-400 rounded-xl font-semibold hover:bg-blue-500 hover:text-white transition-all flex flex-col items-start gap-1"
            >
              <span className="text-sm">Partir plus tôt (09:00)</span>
              <span className="text-[10px] opacity-80 font-normal">
                Toutes les activités incluses en 1 journée — tarif réduit
              </span>
            </button>
          )}

          {/* Option: 1 day, ranking required */}
          <button
            onClick={() => setSelectedDays(1)}
            className={`w-full p-3.5 border rounded-xl font-semibold transition-all text-left ${
              selectedDays === 1
                ? 'bg-emerald-500/20 border-emerald-500 text-white'
                : 'bg-zinc-900 border-zinc-800 text-white hover:border-zinc-600'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm">1 journée</span>
              {selectedDays === 1 && <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />}
            </div>
            <span className="text-[10px] text-amber-400 mt-0.5 block">
              Vous classerez vos activités par priorité — les moins importantes seront retirées
            </span>
          </button>

          {/* Multi-day options */}
          {multiDayOptions.map(days => {
            const isIdeal = Math.abs(days - idealDays) < 0.01;
            const isBelow = days < idealDays;
            const isSelected = selectedDays !== null && Math.abs(selectedDays - days) < 0.01;

            return (
              <button
                key={days}
                onClick={() => setSelectedDays(days)}
                className={`w-full p-3.5 border rounded-xl font-semibold transition-all text-left ${
                  isSelected
                    ? 'bg-emerald-500/20 border-emerald-500 text-white'
                    : isIdeal
                      ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/20'
                      : 'bg-zinc-900/80 border-zinc-800 text-zinc-300 hover:border-zinc-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{formatDays(days)}</span>
                    {isIdeal && (
                      <span className="text-[10px] bg-emerald-500/30 text-emerald-300 px-2 py-0.5 rounded-full font-bold">
                        Recommandé
                      </span>
                    )}
                  </div>
                  {isSelected && <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />}
                </div>
                <span className={`text-[10px] mt-0.5 block ${isBelow ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {isBelow
                    ? 'Vous classerez vos activités par priorité — les moins importantes seront retirées'
                    : 'Toutes vos activités sélectionnées seront incluses'
                  }
                </span>
              </button>
            );
          })}
        </div>

        {/* Per-day departure times for days 2+ */}
        {selectedDays !== null && selectedDays > 1 && (
          <div className="space-y-3 pt-2">
            <h3 className="text-sm font-bold text-zinc-300 flex items-center gap-2">
              <Clock className="w-4 h-4 text-emerald-400" />
              Heures de départ des jours suivants
            </h3>
            {extraDaysNeeded.map(dayNum => (
              <div
                key={dayNum}
                className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-xl p-3"
              >
                <span className="text-sm text-zinc-300 font-semibold">Jour {dayNum}</span>
                <input
                  type="time"
                  value={departureTimes[dayNum] || ''}
                  onChange={e => handleDepartureTimeChange(dayNum, e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 bg-zinc-950/95 backdrop-blur-xl border-t border-white/10">
        <div className="max-w-7xl mx-auto p-4 md:p-6 flex gap-3">
          <button
            onClick={handleBack}
            className="flex-1 py-3 bg-zinc-900 text-zinc-400 rounded-xl font-semibold text-sm hover:bg-zinc-800 border border-zinc-800 transition-colors"
          >
            Retour aux activités
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            className={`flex-[2] py-3 rounded-xl font-semibold text-sm transition-all ${
              isValid
                ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-[0_0_15px_rgba(16,185,129,0.3)]'
                : 'bg-zinc-800 text-zinc-500 border border-zinc-700 cursor-not-allowed'
            }`}
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
};
